import { useEffect, useRef, type ReactNode } from 'react'

interface Props {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}

export function Modal({ title, onClose, children, footer }: Props) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    // Move focus into the dialog so keyboard and screen-reader users land here.
    ref.current?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  /*
   * Tell the page a dialog is up.
   *
   * On a phone a modal is a bottom sheet flush with the bottom of the screen,
   * which is exactly where the tab bar and the feedback tab live. The scrim
   * already makes both untappable, so all they do is show through underneath
   * the sheet's own footer -- "Got it" landing on top of "More". Counted
   * rather than set, so closing one of two stacked dialogs does not bring
   * them back early.
   */
  useEffect(() => {
    const root = document.documentElement
    const open = Number(root.dataset.modals ?? '0') + 1
    root.dataset.modals = String(open)
    return () => {
      const left = Number(root.dataset.modals ?? '1') - 1
      if (left > 0) root.dataset.modals = String(left)
      else delete root.dataset.modals
    }
  }, [])

  /*
   * Keep the sheet above the on-screen keyboard.
   *
   * The overlay is `position: fixed; inset: 0`, which is the *layout*
   * viewport, and neither iOS nor Android shrinks that when a keyboard opens
   * -- only the visual viewport moves. So a bottom sheet pins itself to a
   * bottom that is now behind the keyboard. The feedback form showed it
   * worst, because it focuses its text box on open: the keyboard was up
   * before anyone had seen the sheet, leaving the category buttons cut off
   * and the Send button somewhere under the letter G.
   *
   * visualViewport measures the part still on screen. What is missing from
   * the bottom is the keyboard, and the overlay pulls its own bottom up by
   * that much. Zero everywhere else, so desktop is untouched.
   */
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const root = document.documentElement
    const apply = () => {
      const hidden = window.innerHeight - vv.height - vv.offsetTop
      // A collapsing URL bar moves this by a few pixels and is not a
      // keyboard. Only a real bite counts, and it is never negative.
      root.style.setProperty('--kb', hidden > 80 ? `${Math.round(hidden)}px` : '0px')
    }
    apply()
    vv.addEventListener('resize', apply)
    vv.addEventListener('scroll', apply)
    return () => {
      vv.removeEventListener('resize', apply)
      vv.removeEventListener('scroll', apply)
      // The counting effect above is declared first, so its cleanup has
      // already run: no marker left means this was the last dialog.
      if (!root.dataset.modals) root.style.removeProperty('--kb')
    }
  }, [])

  return (
    <div
      className="overlay"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={ref}
      >
        <div className="modal-head">
          <h2>{title}</h2>
          <div className="spacer" />
          <button type="button" className="btn ghost icon" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  )
}
